// @ts-nocheck
import { For, Show, createEffect, createMemo, createSignal } from "solid-js";
import { saveAs } from 'file-saver';
import { useUserContext } from "./UserContext";
import {
    calculateStudentGrades,
    getFormulaElements,
    getScoreBadgeClass,
    getSummaryFormula,
} from './gradeUtils';

// CSV helpers: RFC-4180 escaping and CRLF line endings
function csvEscape(value) {
    if (value === null || value === undefined) value = '';
    const s = String(value).replace(/\"/g, '""');
    return `"${s}"`;
}
const CRLF = "\r\n";
export default (props) => {
    const [userStore, setUserStore, userActions] = useUserContext();
    const [filterQuery, setFilterQuery] = createSignal('');
    const [ordering, setOrdering] = createSignal({col:'_name', asc:1});

    const topElements = createMemo(() => {
        return getFormulaElements(getSummaryFormula(props.project.summary_template));
    });

    const notes = createMemo(() => {
        const notesObj = {};
        for(const student of Object.keys(props.project.pages || {})) {
            const grades = calculateStudentGrades(props.project, student);
            notesObj[student] = { ...grades.sections, _total: grades.total };
        }
        return notesObj;
    });

    const average_notes = createMemo(() => {
        const average = {};
        const notes_memo = notes();
        const totalStudents = Object.keys(notes_memo).length;
        if (totalStudents === 0) return average;

        let keys = {};
        for(let n of Object.values(notes_memo)) {
            for(let el of Object.keys(n)) {
                if(!average[el]) average[el] = 0;
                average[el] += (n[el] || 0);
                if(!keys[el]) keys[el] = true;
            }
        }
        for(let el of Object.keys(keys)) {
            average[el] /= totalStudents;
            average[el] = Math.round(average[el] * 100) / 100;
        }
        return average;
    });

    const sorted_names = createMemo(() => {
        const pages = Object.keys(props.project.pages || {});
        let filtered = pages;
        if (filterQuery().trim()) {
            const q = filterQuery().toLowerCase();
            filtered = pages.filter(p => p.toLowerCase().includes(q));
        }

        if(ordering().col === '_name' || !topElements().includes(ordering().col)) {
            if (ordering().col === '_total') {
                return filtered.sort((a, b) => ordering().asc * ((notes()[a]?._total || 0) - (notes()[b]?._total || 0)));
            }
            return filtered.sort((a, b) => ordering().asc * (a.localeCompare(b)));
        }
        return filtered.sort((a, b) => ordering().asc * ((notes()[a]?.[ordering().col] || 0) - (notes()[b]?.[ordering().col] || 0)));
    });

    const exportCSV = () => {
        const delimiter = ',';
        const headers = ['Student', ...topElements(), 'Total', 'Feedback'];
        const rows = [headers];

        for (let student of sorted_names()) {
            let feedbackLines = [];
            for (let el of topElements()) {
                if (el === 'Travail') continue;

                const tpl = (props.project.student_template || []).find((e) => e.name == el);
                let subEls = [];
                if (tpl) {
                    const subFormula = tpl.note;
                    subEls = getFormulaElements(subFormula, tpl.cells) || [];
                } else {
                    const keys = Object.keys(props.project.pages[student] || {})
                        .filter((k) => k.startsWith(el+':') && k.endsWith(':comment'))
                        .map((k) => k.replace(el+':','').replace(':comment',''))
                        .sort((a,b)=>a.localeCompare(b));
                    subEls = keys;
                }

                let sectionStarted = false;
                for (let subEl of subEls) {
                    const key = `${el}:${subEl}:comment`;
                    const v = props.project.pages[student]?.[key]?.value;
                    if (v !== undefined && v !== null && String(v).trim() !== '') {
                        if (!sectionStarted) {
                            feedbackLines.push(`# ${el}`);
                            sectionStarted = true;
                        }
                        feedbackLines.push(`  - ${subEl}: ${String(v)}`);
                    }
                }
                if (sectionStarted) feedbackLines.push('');
            }

            const feedbackText = feedbackLines.join(CRLF);
            const total = notes()[student]?._total ?? '';
            const rowData = [student];
            for (let el of topElements()) {
                rowData.push(notes()[student]?.[el] ?? '');
            }
            rowData.push(total);
            rowData.push(feedbackText);
            rows.push(rowData);
        }

        const bom = '\uFEFF';
        const csv = bom + rows.map(r => r.map(csvEscape).join(delimiter)).join(CRLF) + CRLF;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `${props.project.name || 'notes'}-summary.csv`);
    };

    const toggleSort = (col) => {
        setOrdering({
            col,
            asc: ordering().col === col ? -1 * ordering().asc : 1
        });
    };

    const renderSortIcon = (col) => {
        if (ordering().col !== col) {
            return <i class="bi bi-arrow-down-up text-muted opacity-50 small"></i>;
        }
        return ordering().asc === 1 
            ? <i class="bi bi-sort-numeric-down text-primary small"></i>
            : <i class="bi bi-sort-numeric-down-alt text-primary small"></i>;
    };

    return (
        <div class="summary-container">
            {/* Header & Action Bar */}
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div>
                    <h2 class="h4 fw-bold text-dark mb-1">Tableau récapitulatif des notes</h2>
                    <p class="text-muted small mb-0">
                        {sorted_names().length} étudiant(s) évalué(s) • Moyenne générale : <strong class="text-primary">{average_notes()._total ?? '-'} / 20</strong>
                    </p>
                </div>
                
                <div class="d-flex align-items-center gap-2">
                    <div class="input-group" style="width: 220px;">
                        <span class="input-group-text bg-white border-end-0 text-muted"><i class="bi bi-search"></i></span>
                        <input
                            type="text"
                            class="form-control border-start-0 ps-0"
                            placeholder="Filtrer un étudiant..."
                            value={filterQuery()}
                            onInput={(e) => setFilterQuery(e.currentTarget.value)}
                        />
                    </div>
                    
                    <button
                        type="button"
                        class="btn btn-outline-primary d-inline-flex align-items-center gap-2 shadow-sm"
                        onClick={exportCSV}
                    >
                        <i class="bi bi-download"></i>
                        <span>Exporter CSV</span>
                    </button>
                </div>
            </div>

            {/* Table */}
            <div class="summary-table-wrapper">
                <table class="table summary-table table-hover align-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col">
                                <a href="#" onClick={(e) => { e.preventDefault(); toggleSort('_name'); }}>
                                    <span>Étudiant</span>
                                    {renderSortIcon('_name')}
                                </a>
                            </th>
                            <For each={topElements()}>
                                {(element) => (
                                    <th scope="col" class="text-center">
                                        <a href="#" onClick={(e) => { e.preventDefault(); toggleSort(element); }}>
                                            <span>{element}</span>
                                            {renderSortIcon(element)}
                                        </a>
                                    </th>
                                )}
                            </For>
                            <th scope="col" class="text-center">
                                <a href="#" onClick={(e) => { e.preventDefault(); toggleSort('_total'); }}>
                                    <span>Note Finale (/20)</span>
                                    {renderSortIcon('_total')}
                                </a>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={sorted_names()}>
                            {(student) => (
                                <tr>
                                    <th scope="row">
                                        <a
                                            href="#"
                                            class="text-decoration-none fw-semibold text-dark d-inline-flex align-items-center gap-2"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                userActions.setPage('/projects/' + props.project.id + '/' + student);
                                                userActions.setLastSelectedStudent(student);
                                            }}
                                        >
                                            <i class="bi bi-person-circle text-muted"></i>
                                            <span>{student}</span>
                                        </a>
                                    </th>
                                    <For each={topElements()}>
                                        {(element) => (
                                            <td class="text-center">
                                                <span class={`score-badge ${getScoreBadgeClass(notes()[student]?.[element])}`}>
                                                    {notes()[student]?.[element] ?? '-'}
                                                </span>
                                            </td>
                                        )}
                                    </For>
                                    <td class="text-center">
                                        <span class={`score-badge fw-bold px-2.5 py-1 ${getScoreBadgeClass(notes()[student]?._total)}`}>
                                            {notes()[student]?._total ?? '-'}
                                        </span>
                                    </td>
                                </tr>
                            )}
                        </For>
                        <tr class="average-row">
                            <td class="fw-bold">
                                <i class="bi bi-calculator me-2 text-primary"></i>
                                Moyenne de la promotion
                            </td>
                            <For each={topElements()}>
                                {(element) => (
                                    <td class="text-center fw-bold">
                                        {average_notes()[element] ?? '-'}
                                    </td>
                                )}
                            </For>
                            <td class="text-center fw-bold text-primary fs-6">
                                {average_notes()._total ?? '-'}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
